// Tableau d'état (issue #604, lot N3 de la nuit du 08/10).
//
// Cyrille, 07/10 et 08/10 : « des tuiles pour des documents d'urbanisme : oui, non, non,
// non… j'avais demandé que ce soit plutôt en tableau. Le tableau pourrait avoir une colonne
// comprendre ce chiffre. » Marc : « si c'est la même section, il faut que ça soit un tableau
// parce qu'il n'y a pas de comparaison par strate. »
//
// Le backend (`socle/vue.py`, `_tableau_etat`) regroupe, à la commune, les indicateurs de
// présence d'une section (≥ 2) en UN bloc `chart.type === 'etat'` ; `ChartCard` le rend ici,
// jamais par Chart.js (pas de canvas : c'est du texte, lisible tel quel par un lecteur d'écran
// et imprimé tel quel dans le PDF).
//
// Colonnes : Indicateur · État (badge Oui / Non / « non renseigné ») · Millésime · « Comprendre
// ce chiffre ». Le bouton (i) de chaque ligne ouvre le MÊME panneau qu'une tuile
// (`PanneauExplication`, avec la fiche et le terme du glossaire que la tuile portait).
// Petit écran : le tableau défile horizontalement dans son cadre, la page ne bouge pas.
import { useCallback, useRef, useState } from 'react'
import { Info } from 'lucide-react'
import { PanneauExplication } from './Explication'
import type { LigneEtat, SourceDetail } from './types'
import './TableauEtat.css'

function Badge({ valeur }: { valeur: LigneEtat['valeur'] }) {
  if (valeur === 'Oui') return <span className="obs2-etat-badge" data-etat="oui">Oui</span>
  if (valeur === 'Non') return <span className="obs2-etat-badge" data-etat="non">Non</span>
  // N·D : aucune valeur pour cette commune — dit tel quel, jamais « Non » par défaut.
  return <span className="obs2-etat-badge" data-etat="nd">non renseigné</span>
}

function Comprendre({ ligne, sources }: { ligne: LigneEtat; sources?: SourceDetail[] }) {
  const [ouvert, setOuvert] = useState(false)
  const declencheur = useRef<HTMLButtonElement>(null)
  const fermer = useCallback(() => {
    setOuvert(false)
    // Le focus revient au (i) de la ligne, comme à la fermeture du panneau d'une tuile.
    declencheur.current?.focus()
  }, [])
  return (
    <>
      <button ref={declencheur} type="button" className="obs2-etat-i"
        aria-haspopup="dialog" aria-label={`Comprendre ce chiffre : ${ligne.label}`}
        onClick={e => { e.stopPropagation(); setOuvert(true) }}>
        <Info size={14} aria-hidden="true" />
      </button>
      {ouvert && (
        <PanneauExplication onClose={fermer} cible={{
          titre: ligne.label, defTerm: ligne.def_term, fiche: ligne.fiche,
          note: ligne.note, sources,
        }} />
      )}
    </>
  )
}

export default function TableauEtat({ lignes, titre, sources, explicable = true }: {
  lignes: LigneEtat[]; titre: string; sources?: SourceDetail[]; explicable?: boolean
}) {
  if (!lignes.length) {
    return <p className="obs2-card-note">Donnée non disponible pour cette commune (N/D).</p>
  }
  return (
    <div className="obs2-etat-cadre" role="region" aria-label={titre} tabIndex={0}>
      <table className="obs2-etat">
        <thead>
          <tr>
            <th scope="col">Indicateur</th>
            <th scope="col">État</th>
            <th scope="col">Millésime</th>
            {/* Imprimé (PrintView), la colonne n'existe pas : rien à cliquer sur papier. */}
            {explicable ? <th scope="col">Comprendre ce chiffre</th> : null}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l, i) => (
            <tr key={`${l.fiche ?? l.label}-${i}`}>
              <th scope="row">{l.label}</th>
              <td><Badge valeur={l.valeur} /></td>
              <td className="obs2-etat-mil">{l.millesime ?? '—'}</td>
              {explicable ? (
                <td className="obs2-etat-col-i">
                  <Comprendre ligne={l} sources={sources} />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
